import { useState, type FormEvent, type JSX } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Alert, AlertDescription, Button, Input, Label, Spinner } from "@mailflare/ui";

import { routes } from "@/features/mailboxes/routes";

import { useVerifyTotp } from "./hooks";
import { AuthCard, errorText } from "./components/auth-card";

interface TotpLocationState {
  readonly challengeId?: string;
  readonly from?: string;
}

export function TotpPage(): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as TotpLocationState | null;
  const [code, setCode] = useState("");
  const verify = useVerifyTotp();

  if (state?.challengeId === undefined) {
    return (
      <AuthCard title="Two-factor required" description="Start again from the sign-in screen.">
        <Button className="w-full" onClick={() => navigate(routes.login, { replace: true })}>
          Back to sign in
        </Button>
      </AuthCard>
    );
  }

  const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    verify.mutate(
      { challengeId: state.challengeId as string, code },
      {
        onSuccess: () => {
          navigate(state.from ?? routes.root, { replace: true });
        },
      },
    );
  };

  return (
    <AuthCard
      title="Two-factor authentication"
      description="Enter the 6-digit code from your authenticator app."
    >
      <form className="space-y-4" onSubmit={handleSubmit}>
        <div className="space-y-1.5">
          <Label htmlFor="totp-code">Verification code</Label>
          <Input
            id="totp-code"
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="\d{6}"
            maxLength={6}
            required
            value={code}
            onChange={(event) => setCode(event.target.value.replace(/\D/gu, ""))}
          />
        </div>

        {verify.error !== null ? (
          <Alert variant="destructive">
            <AlertDescription>{errorText(verify.error)}</AlertDescription>
          </Alert>
        ) : null}

        <Button type="submit" className="w-full" disabled={verify.isPending}>
          {verify.isPending ? <Spinner aria-hidden className="size-4" /> : null}
          Verify
        </Button>
      </form>
    </AuthCard>
  );
}
